import React, { useEffect, useMemo, useRef } from 'react';
import './FireworksOverlay.css';

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function rand(min, max) {
  return Math.random() * (max - min) + min;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Fullscreen fireworks overlay (canvas).
 * - pointer-events: none (won't block UI)
 * - auto stops after durationMs, calls onComplete
 */
export default function FireworksOverlay({ active, durationMs = 10000, onComplete }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const timeoutRef = useRef(null);
  const particlesRef = useRef([]);
  const fireworksRef = useRef([]);
  const lastSpawnRef = useRef(0);
  const startTimeRef = useRef(0);
  const completedRef = useRef(false);
  const dprRef = useRef(1);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    if (!active) return;
    if (prefersReducedMotion) {
      // Respect reduced motion by immediately completing.
      onComplete?.();
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const FADE_OUT_MS = 3200; // after durationMs, gradually reduce spawns and fade out

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      dprRef.current = clamp(dpr, 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = Math.floor(w * dprRef.current);
      canvas.height = Math.floor(h * dprRef.current);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dprRef.current, 0, 0, dprRef.current, 0, 0);
    };

    resize();
    window.addEventListener('resize', resize);

    const COLORS = [
      '#f59e0b', // gold
      '#fbbf24',
      '#ef4444', // red
      '#fb7185',
      '#22c55e',
      '#38bdf8',
      '#a78bfa',
      '#ffffff',
    ];

    const getIntensity = (elapsedMs) => {
      if (elapsedMs <= durationMs) return 1;
      const t = (elapsedMs - durationMs) / FADE_OUT_MS;
      return clamp(1 - t, 0, 1);
    };

    const spawnFirework = (now, intensity = 1) => {
      const w = window.innerWidth;
      const h = window.innerHeight;

      // Bias bursts toward upper half.
      const x = rand(w * 0.12, w * 0.88);
      const y = rand(h * 0.12, h * 0.55);
      const color = pick(COLORS);

      fireworksRef.current.push({
        x,
        y,
        color,
        createdAt: now,
        life: rand(240, 420), // ms for bloom ring
        radius: rand(10, 26),
      });

      const count = Math.max(12, Math.floor(rand(70, 120) * intensity));
      const baseSpeed = rand(2.2, 4.4) * (0.85 + 0.15 * intensity);
      for (let i = 0; i < count; i++) {
        const a = (Math.PI * 2 * i) / count + rand(-0.08, 0.08);
        const speed = baseSpeed * rand(0.6, 1.15);
        particlesRef.current.push({
          x,
          y,
          vx: Math.cos(a) * speed,
          vy: Math.sin(a) * speed,
          color,
          alpha: 1,
          size: rand(1.1, 2.2),
          decay: rand(0.010, 0.018),
          gravity: rand(0.035, 0.055),
          drag: rand(0.985, 0.992),
          twinkle: Math.random() < 0.12,
        });
      }
    };

    const draw = (t) => {
      if (!startTimeRef.current) startTimeRef.current = t;
      const elapsed = t - startTimeRef.current;
      const intensity = getIntensity(elapsed);

      // trails fade WITHOUT darkening the page:
      // "destination-out" erases existing pixels (to transparent) gradually.
      ctx.globalCompositeOperation = 'destination-out';
      // slightly faster fade during the tail so the canvas clears smoothly
      const fadeAlpha = 0.20 + (1 - intensity) * 0.22;
      ctx.fillStyle = `rgba(0, 0, 0, ${fadeAlpha.toFixed(3)})`;
      ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);

      // spawn every 260~520ms
      if (intensity > 0.01) {
        const baseGap = rand(260, 520);
        const gap = baseGap / Math.max(intensity, 0.12);
        if (t - lastSpawnRef.current > gap) {
          lastSpawnRef.current = t;
          spawnFirework(t, intensity);
          // occasional double burst (less likely in tail)
          const doubleChance = 0.35 * intensity;
          if (Math.random() < doubleChance) {
            spawnFirework(t, intensity);
          }
        }
      }

      // bloom rings
      ctx.globalCompositeOperation = 'source-over';
      const fw = fireworksRef.current;
      for (let i = fw.length - 1; i >= 0; i--) {
        const f = fw[i];
        const age = t - f.createdAt;
        const p = clamp(age / f.life, 0, 1);
        const r = f.radius + p * 36;
        const a = (1 - p) * 0.55;
        ctx.beginPath();
        ctx.strokeStyle = f.color;
        ctx.globalAlpha = a;
        ctx.lineWidth = 2;
        ctx.arc(f.x, f.y, r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;
        if (age >= f.life) fw.splice(i, 1);
      }

      // particles
      ctx.globalCompositeOperation = 'lighter';
      const ps = particlesRef.current;
      for (let i = ps.length - 1; i >= 0; i--) {
        const p = ps[i];
        p.vx *= p.drag;
        p.vy *= p.drag;
        p.vy += p.gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.alpha -= p.decay;
        if (p.twinkle && Math.random() < 0.16) {
          p.alpha = Math.min(1, p.alpha + 0.15);
        }

        if (p.alpha <= 0) {
          ps.splice(i, 1);
          continue;
        }

        ctx.globalAlpha = clamp(p.alpha, 0, 1);
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';

      // Finish only after the tail has run and particles are basically gone.
      if (!completedRef.current && elapsed >= durationMs + FADE_OUT_MS) {
        if (particlesRef.current.length === 0 && fireworksRef.current.length === 0) {
          completedRef.current = true;
          onComplete?.();
          return;
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    // kick off
    lastSpawnRef.current = 0;
    startTimeRef.current = 0;
    completedRef.current = false;
    // clear to transparent and start
    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    rafRef.current = requestAnimationFrame(draw);

    // Hard stop as a safety net (should normally finish earlier by clearing particles)
    timeoutRef.current = window.setTimeout(() => {
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete?.();
      }
    }, durationMs + FADE_OUT_MS + 1500);

    return () => {
      window.removeEventListener('resize', resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      particlesRef.current = [];
      fireworksRef.current = [];
      startTimeRef.current = 0;
      completedRef.current = false;
      // best-effort clear
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    };
  }, [active, durationMs, onComplete, prefersReducedMotion]);

  if (!active) return null;

  return (
    <div className="fireworks-overlay" aria-hidden="true">
      <canvas ref={canvasRef} />
    </div>
  );
}


